import {useEffect, useRef, useState, useReducer} from "react";
import * as tus from "tus-js-client";
import {debounce, listify} from "radash";


export const useTusJsClient = ({
    endpoint,
    path,
    onSuccess,
    onError,
    retryDelays=[0, 3000, 5000, 10000, 20000],
    logLevel = 1
}) => {


    const uploadInput = useRef(null);
    const [isInProgress, setIsInProgress] = useState(null)
    const [isComplete, setIsComplete] = useState(null)
    const [bytesTotal, setBytesTotal] = useState(null)
    const [bytesUploaded, setBytesUploaded] = useState(null)
    const [percentage, setPercentage] = useState(null)
    const [error, setError] = useState(null)
    const [fileName, setFileName] = useState(null)
    const [fileCount, setFileCount] = useState(null)
    const [filesSent, filesSentInc] = useReducer(i => i + 1, 0)

    const [isInitialized, setIsInitialized] = useState(false)

    useEffect(() => {

        if(isInitialized) {
            return
        }

        if(uploadInput.current !== null) {
            initInput(uploadInput.current)
            setIsInitialized(true)
        }
    }, [uploadInput.current])


    const initInput = input =>
        input.addEventListener('change', e => {
            setFileCount(e.target.files.length)
            uploadNextFile(e.target.files)
        })


    const uploadNextFile = fileList => {

        const [first, ...remainder] = fileList

        if(remainder.length === 0) {
            uploadFile(first, () => {})
        }
        else {
            uploadFile(first, () => uploadNextFile(remainder))
        }
    }


    const uploadFile = (file, transferComplete) => {
            setFileName(file.name)

            let chunkCount = 1

            const upload = new tus.Upload(file, {
                endpoint: endpoint,
                retryDelays: retryDelays,
                metadata: {
                    filename: file.name,
                    filetype: file.type,
                    path
                },
                onError: function (error) {
                  setError(error)
                    if(logLevel > 0) {
                        console.log(error)
                    }
                },
                onChunkComplete: function (chunkSize, bytesAccepted, bytesTotal) {
                    if(logLevel >= 1) {
                        chunkCount += 1
                        console.log(`chunk complete: ${chunkCount} ${chunkSize}, ${bytesAccepted}, ${bytesTotal}`)
                    }
                },
                onProgress: function (bytesUploaded, bytesTotal) {
                    const percentage = ((bytesUploaded / bytesTotal) * 100).toFixed(0)
                    if(logLevel >= 1) {
                        console.log(`% ${percentage} uploaded`)
                    }

                    setIsInProgress(true)
                    setBytesTotal(bytesTotal)
                    setBytesUploaded(bytesUploaded)
                    setPercentage(percentage)
                },
                onSuccess: function () {
                    setIsComplete(true)
                    setIsInProgress(false)
                    filesSentInc()
                    if(onSuccess) {
                        onSuccess()
                    }
                    transferComplete()
                }
            })

            setIsComplete(false)

            if(logLevel >= 1) {
                console.log("upload started")
            }
            upload.start()
    }

    return {
        fileName,
        uploadInput,
        isInProgress,
        isComplete,
        bytesTotal,
        bytesUploaded,
        percentage,
        error,
        clickUpload: debounce({delay: 1}, () => uploadInput.current.click())
    }
}



const passwordAndConfirmPreReducer = (state, action) => {


    switch (action.name) {
        case "init": {
            return {
                ...state,
                password: '',
                passwordConfirm: ''
            }
        }

        case "updatePassword": {

            const password = action.password
            return {
                ...state,
                password: password,
                tooShort: password.length < state.minLength,
                noDigit: !/\d/.test(password),
                noLowercase: !/[a-z]/.test(password),
                noUpercase: !/[A-Z]/.test(password),
                noSpecialCaracter: !/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
            }
        }
        case "updatePasswordConfirm": {
            return {
                ...state,
                passwordConfirm: action.passwordConfirm
            }
        }
        default:
            throw Error(`unknown action : '${action.name}'`)
    }
}

export const passwordAndConfirmReducer = (state, action) => {

    const nextState = passwordAndConfirmPreReducer(state, action)

    const pwStrengthWarnings = {
      tooShort: nextState.tooShort,
      noDigit: nextState.noDigit,
      noLowercase: nextState.noLowercase,
      noUpercase: nextState.noUpercase,
      noSpecialCaracter: nextState.noSpecialCaracter
    }

    const pkStrengthMessages = {
      tooShort: `at least ${state.minLength} characters long`,
      noDigit: 'at least one digit',
      noLowercase: 'at least one lowercase character',
      noUpercase: 'at least one uppercase character',
      noSpecialCaracter: 'at least one special character (ex: $,-,!,@, etc.)'
    }


    const problemList =
      listify(pwStrengthWarnings, (k, v) => v ? pkStrengthMessages[k]: null).filter(i=>i)

    const passwordStrengthWeak = problemList.length > 0

    const warnFieldsDiffer = (nextState.passwordConfirm !== "") && nextState.passwordConfirm !== nextState.password

    const canReset = !(
        passwordStrengthWeak || warnFieldsDiffer || problemList.length > 0 ||
            nextState.password === ""
    )

    return {
        ...nextState,
        problemList,
        passwordStrengthWeak,
        warnFieldsDiffer,
        canReset
    }
}



export const usePasswordAndConfirmFields = minLength => {

    const [passwordAndConfirmView, passwordAndConfirmDispatch] = useReducer(
        passwordAndConfirmReducer, null,
        () => passwordAndConfirmReducer({minLength}, {name: 'init'})
    )

    return {
        ...passwordAndConfirmView,
        setPassword: password => passwordAndConfirmDispatch({name: 'updatePassword', password}),
        setPasswordConfirm: passwordConfirm => passwordAndConfirmDispatch({name: 'updatePasswordConfirm', passwordConfirm})
    }
}
